/**
 * Endpoint Manager con Failover
 *
 * Maneja la selección automática de endpoints de API con health checks,
 * rotación automática y persistencia del último endpoint funcional.
 */

import config from '../config';

const STORAGE_KEY = 'api_endpoints_order';
const HEALTH_CHECK_TIMEOUT = 1000; // 1 segundo

// Endpoints por defecto en orden de prioridad (desde config)
const DEFAULT_ENDPOINTS = config.ROUTES_API;

/**
 * Obtiene la lista de endpoints ordenada.
 * Usa la lista guardada si existe, sino la default.
 */
export function getEndpoints(): string[] {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length === DEFAULT_ENDPOINTS.length) {
        return parsed;
      }
    }
  } catch {
    // Si hay error al parsear, usar default
  }
  return [...DEFAULT_ENDPOINTS];
}

/**
 * Guarda el nuevo orden de endpoints.
 */
function saveEndpoints(endpoints: string[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(endpoints));
  } catch {
    // Silenciar errores de storage
  }
}

/**
 * Mueve un endpoint al principio de la lista y guarda el nuevo orden.
 */
function moveToFirst(endpoints: string[], index: number): string[] {
  if (index === 0) return endpoints;

  const newOrder = [...endpoints];
  const [endpoint] = newOrder.splice(index, 1);
  newOrder.unshift(endpoint);
  saveEndpoints(newOrder);
  return newOrder;
}

/**
 * Genera el timestamp para el health check (formato: YYYYMMDDHHmmss)
 */
function getTimestamp(): string {
  const now = new Date();
  return now.getFullYear().toString() +
    (now.getMonth() + 1).toString().padStart(2, '0') +
    now.getDate().toString().padStart(2, '0') +
    now.getHours().toString().padStart(2, '0') +
    now.getMinutes().toString().padStart(2, '0') +
    now.getSeconds().toString().padStart(2, '0');
}

/**
 * Realiza un health check a un endpoint con timeout.
 * Retorna true si responde 200 en menos de 1 segundo.
 */
async function healthCheck(endpoint: string): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

  try {
    const url = `${endpoint}health.php?${getTimestamp()}`;
    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-cache',
    });

    clearTimeout(timeoutId);
    return response.status === 200;
  } catch {
    clearTimeout(timeoutId);
    return false;
  }
}

/**
 * Obtiene el endpoint activo realizando health checks.
 *
 * Lógica:
 * 1. Obtiene la lista de endpoints (guardada o default)
 * 2. Para cada endpoint en orden, hace health check
 * 3. Si responde 200 en < 1s, lo usa y lo mueve al principio si no lo estaba
 * 4. Si ninguno funciona, devuelve el primero como fallback
 * 5. Guarda el nuevo orden para el próximo request
 */
export async function getActiveEndpoint(): Promise<string> {
  const endpoints = getEndpoints();

  for (let i = 0; i < endpoints.length; i++) {
    const endpoint = endpoints[i];
    const isHealthy = await healthCheck(endpoint);

    if (isHealthy) {
      // Si no era el primero, moverlo al principio
      if (i > 0) {
        moveToFirst(endpoints, i);
      }
      return endpoint;
    }
  }

  // Fallback: devolver el primero de la lista
  return endpoints[0];
}

/**
 * Versión síncrona que devuelve el primer endpoint guardado.
 * Útil para requests rápidos donde no se quiere esperar el health check.
 */
export function getPreferredEndpoint(): string {
  const endpoints = getEndpoints();
  return endpoints[0];
}

/**
 * Marca un endpoint como fallido y rota al siguiente.
 * Útil cuando un request falla después del health check.
 */
export function markEndpointFailed(failedEndpoint: string): string {
  const endpoints = getEndpoints();
  const failedIndex = endpoints.indexOf(failedEndpoint);

  if (failedIndex > -1 && failedIndex < endpoints.length - 1) {
    // Mover el siguiente endpoint al principio
    moveToFirst(endpoints, failedIndex + 1);
    return endpoints[failedIndex + 1];
  }

  // Si era el último, devolver el primero
  return endpoints[0];
}

/**
 * Resetea la lista de endpoints al orden por defecto.
 */
export function resetEndpoints(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export { DEFAULT_ENDPOINTS };
